/**
 * Approval Service
 * AIDLC Administrative Backend
 *
 * Pending agent approval requests and the answers to them, through the shared apiClient so every
 * call carries the signed-in user's token (APPROVAL-TOKEN-1, BL-beb6a8cc). The server allows these
 * routes to Admin and Operator only (APPROVER_ROLES in server/src/index.ts).
 */

import axios from 'axios';
import apiClient from './api';

const APPROVALS_PATH = '/approvals';

export type ApprovalDecision = 'approved' | 'denied' | 'instructions';

export interface ApprovalRequest {
  id: string;
  agentId: string;
  type: 'tool_denied' | 'action_approval' | 'direction_needed' | 'review_required';
  title: string;
  description: string;
  options: string[];
  context: Record<string, unknown>;
  status: string;
  timestamp: string;
}

/** Shown when the list of waiting approvals could not be loaded (MAYA-AW-02, BL-86788e85); a Try again button sits beside it. */
export const APPROVALS_LOAD_ERROR = 'We could not load the approvals waiting for you.';

const ANSWER_ERRORS: Record<number, string> = {
  // Each answer fetches a fresh token, so answering again is the first step; it keeps any typed instructions.
  401: 'Your answer was not sent because your sign-in has expired. Answer again; if this message comes back, sign out and sign in again.',
  403: 'Your answer was not sent because your account cannot answer approvals. Ask an Admin.',
  404: 'This request is no longer waiting for an answer. Refresh the page to update the list.',
};

const ANSWER_ERROR_DEFAULT = 'Your answer was not sent. Check your connection and try again.';

/** The approvals waiting for an answer. */
export async function getPendingApprovals(): Promise<ApprovalRequest[]> {
  const response = await apiClient.get<ApprovalRequest[]>(APPROVALS_PATH);
  return Array.isArray(response.data) ? response.data : [];
}

/** Sends one answer. Resolves only when the server accepted it (2xx); rejects otherwise. */
export async function respondToApproval(requestId: string, decision: ApprovalDecision, message: string): Promise<void> {
  await apiClient.post(`${APPROVALS_PATH}/${encodeURIComponent(requestId)}/respond`, { decision, message });
}

/** Plain words for a refused or failed answer; the technical detail stays in the console. */
export function approvalAnswerError(error: unknown): string {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return (status !== undefined && ANSWER_ERRORS[status]) || ANSWER_ERROR_DEFAULT;
}
